import React, { useState, useCallback, useRef, useEffect } from "react";
import classNames from "classnames";

import styles from "./ExtensionsSlideshow.module.css";

type PropTypes = {
  slides: React.ReactNode[];
  anchors: React.ReactNode[];
  gradients: string[];
  autoScrollInterval?: number;
};

const ExtensionsSlideshow: React.FC<PropTypes> = ({ slides, anchors, autoScrollInterval = 6000, gradients }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const scrollInterval = useRef<number>();

  const stopAutoScroll = useCallback(() => {
    if (scrollInterval.current !== null) {
      window.clearInterval(scrollInterval.current);
    }
  }, []);

  const restartAutoScroll = useCallback(() => {
    stopAutoScroll();
    if (autoScrollInterval !== null) {
      scrollInterval.current = window.setInterval(() => {
        setActiveSlide((activeSlide) => (activeSlide + 1) % slides.length);
      }, autoScrollInterval);
    }
  }, [stopAutoScroll, autoScrollInterval, setActiveSlide, slides.length]);

  useEffect(() => {
    restartAutoScroll();
    return stopAutoScroll;
  }, [restartAutoScroll, stopAutoScroll]);

  const onChangeSlide = useCallback(
    (slide: number) => {
      setActiveSlide(slide);
      restartAutoScroll();
    },
    [setActiveSlide, restartAutoScroll]
  );

  return (
    <>
      <div
        className={styles.imgContainer}
        style={{ backgroundImage: gradients[activeSlide] }}
        onMouseOver={stopAutoScroll}
        onMouseOut={restartAutoScroll}
      >
        <div className={styles.inner}>
          {slides.map((slide, index) => (
            <div key={index} style={{ marginLeft: index === 0 ? `-${activeSlide * 100}%` : 0 }}>
              {slide}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.anchors} onMouseOver={stopAutoScroll} onMouseOut={restartAutoScroll}>
        {anchors.map((anchor, index) => (
          <a
            className={classNames(styles.anchor, { [styles.anchorActive]: index === activeSlide })}
            key={index}
            href={`#slide-${index}`}
            onClick={(event) => {
              event.preventDefault();
              onChangeSlide(index);
            }}
          >
            {anchor}
          </a>
        ))}
      </div>
    </>
  );
};

export default ExtensionsSlideshow;
